import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Icon } from '@atoms/index';
import type { SearchRestaurantHit, SearchProductHit } from '@/types';
import { formatCurrency } from '@utils/currency';

interface SearchResultsDropdownProps {
  restaurants: SearchRestaurantHit[];
  products: SearchProductHit[];
  query: string;
  isLoading: boolean;
  isVisible: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
}

const SearchResultsDropdown = ({
  restaurants,
  products,
  query,
  isLoading,
  isVisible,
  onClose,
  anchorRef,
}: SearchResultsDropdownProps) => {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const minWidth = 480;
    const width = Math.max(rect.width, minWidth);
    const viewportWidth = window.innerWidth;
    let left = rect.left;
    if (left + width > viewportWidth - 16) {
      left = Math.max(16, viewportWidth - width - 16);
    }
    setPosition({
      top: rect.bottom + 8,
      left,
      width,
    });
  }, [anchorRef]);

  useEffect(() => {
    if (!isVisible) return;
    updatePosition();

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isVisible, updatePosition]);

  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current?.contains(target) ||
        anchorRef.current?.contains(target)
      ) {
        return;
      }
      onClose();
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const hasResults = restaurants.length > 0 || products.length > 0;

  const handleRestaurantClick = (restaurantId: string) => {
    onClose();
    navigate(`/restaurant/${restaurantId}`);
  };

  const handleProductClick = (restaurantId: string) => {
    onClose();
    navigate(`/restaurant/${restaurantId}`);
  };

  return createPortal(
    <>
      <div
        ref={dropdownRef}
        className="fixed bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-x-hidden overflow-y-auto z-[9999] max-h-[70vh]"
        style={{ top: position.top, left: position.left, width: position.width, minWidth: '480px' }}
      >
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="ml-3 text-sm text-gray-500">Aranıyor...</span>
          </div>
        )}

        {!isLoading && !hasResults && query.length >= 2 && (
          <div className="py-8 text-center">
            <Icon name="search" size={32} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm font-medium text-gray-500">
              &ldquo;{query}&rdquo; için sonuç bulunamadı
            </p>
            <p className="text-xs text-gray-400 mt-1">Farklı bir arama terimi deneyin</p>
          </div>
        )}

        {!isLoading && hasResults && (
          <>
            {restaurants.length > 0 && (
              <div>
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Restoranlar ({restaurants.length})
                  </h3>
                </div>
                {restaurants.map((restaurant) => (
                  <button
                    key={restaurant.id}
                    onClick={() => handleRestaurantClick(restaurant.id)}
                    className="w-full flex items-center gap-4 px-4 py-3.5 hover:bg-primary/5 transition-colors text-left border-b border-gray-50 last:border-b-0 group"
                  >
                    <div className={`flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-gray-100 border border-gray-200 shadow-sm ${!restaurant.isAcceptingOrders ? 'opacity-60 grayscale' : ''}`}>
                      {restaurant.bannerUrl ? (
                        <img
                          src={restaurant.bannerUrl}
                          alt={restaurant.name}
                          className="w-full h-full object-cover object-center"
                        />
                      ) : restaurant.logoUrl ? (
                        <img
                          src={restaurant.logoUrl}
                          alt={restaurant.name}
                          className="w-full h-full object-contain object-center p-1"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
                          <Icon name="store" size={24} className="text-primary/50" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p
                          className={`text-sm font-bold truncate group-hover:text-primary transition-colors ${!restaurant.isAcceptingOrders ? 'text-gray-400' : 'text-gray-900'}`}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(restaurant._formatted?.name || restaurant.name, { ALLOWED_TAGS: ['em', 'strong', 'mark'] }),
                          }}
                        />
                        {!restaurant.isAcceptingOrders && (
                          <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-600 rounded-md">
                            Kapalı
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {restaurant.address || restaurant.description}
                      </p>
                      {Number(restaurant.rating) > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <Icon name="star" size={11} className="text-amber-400 fill-amber-400" />
                          <span className="text-xs font-semibold text-amber-600">
                            {Number(restaurant.rating).toFixed(1)}
                          </span>
                          {Number(restaurant.reviewCount) > 0 && (
                            <span className="text-xs text-gray-400">
                              ({Number(restaurant.reviewCount)} değerlendirme)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <Icon name="chevron-right" size={16} className="flex-shrink-0 text-gray-300 group-hover:text-primary transition-colors" />
                  </button>
                ))}
              </div>
            )}

            {products.length > 0 && (
              <div>
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Yemekler ({products.length})
                  </h3>
                </div>
                {products.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleProductClick(product.restaurantId)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-primary/5 transition-colors text-left border-b border-gray-50 last:border-b-0"
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          className="w-full h-full object-contain object-center"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-lg">
                          🍽️
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm font-semibold text-gray-900 truncate"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(product._formatted?.name || product.name, { ALLOWED_TAGS: ['em', 'strong', 'mark'] }),
                        }}
                      />
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {product.restaurantName} &middot; {product.categoryName}
                      </p>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2">
                      <span className="text-sm font-bold text-primary">
                        {formatCurrency(product.price)}
                      </span>
                      <Icon name="chevron-right" size={14} className="text-gray-300" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>,
    document.body,
  );
};

export default SearchResultsDropdown;
